'use client';

import { eventNames, useCoreEvents } from '@carvana/core-events';
import { isSuccess } from '@carvana/merch-result';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { refreshAfterSoftpullAction } from '~/app/(carvana-layout)/financing-rsc/actions';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useIsNative } from '~/context/common/DeviceContext';
import { useModalContext } from '~/context/flp/ModalContext/ModalContext';
import { reportActionFailure } from '~/utilities/flp/reportClientError';

const OPEN_SOFTPULL_QUERY_PARAM = 'openSoftpullModal';

/**
 * Fork of `~/hooks/flp/useSoftpullModalEvents` that calls
 * `refreshAfterSoftpullAction` instead of `refreshTerms()`.
 */
export default function useSoftpullModalEvents() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isNative = useIsNative();
  const { setSoftPullModalOpen } = useModalContext();
  const softpullSuccessState = useRef(false);

  const clearSoftpullQueryParam = () => {
    const params = new URLSearchParams(searchParams?.toString());

    if (params.has(OPEN_SOFTPULL_QUERY_PARAM)) {
      params.delete(OPEN_SOFTPULL_QUERY_PARAM);

      const queryParams = params.toString();
      const newUrl = queryParams ? `${pathname}?${queryParams}` : pathname;

      // Must use native window history to prevent RSC fetching loop caused by reverse proxy
      window.history.replaceState({ scroll: false }, '', newUrl);
    }
  };

  useCoreEvents(eventNames.softpullModal.success, async () => {
    softpullSuccessState.current = true;

    try {
      const result = await refreshAfterSoftpullAction();

      if (!isSuccess(result)) {
        reportActionFailure(
          DATADOG_EVENTS.API.REFRESH_AFTER_SOFTPULL_API_ERROR,
          'Refresh After Softpull',
          result.errorMessage
        );
      }
    } catch {
      reportActionFailure(
        DATADOG_EVENTS.API.REFRESH_AFTER_SOFTPULL_API_ERROR,
        'Refresh After Softpull'
      );
    }
  });

  useCoreEvents(eventNames.softpullModal.close, () => {
    clearSoftpullQueryParam();
    softpullSuccessState.current = false;
  });

  useEffect(() => {
    const autoPopModal = () => {
      const shouldOpenSoftpull =
        searchParams?.has('utm_source') ||
        searchParams?.get(OPEN_SOFTPULL_QUERY_PARAM) === 'true';

      if (
        !isNative &&
        !searchParams?.has('openAccountModal') &&
        shouldOpenSoftpull
      ) {
        setSoftPullModalOpen(true);
      }
    };

    autoPopModal();
  }, [isNative, pathname, searchParams, setSoftPullModalOpen]);
}
